import {
  useDiagnosticAgentState,
  useSuggestionsState,
} from "../../../context/diagnosticAgent";
import { useSuggestions } from "./useSuggestions";
import { useEffect, useMemo } from "react";
import { useDiseasesSections } from "./useDiseasesSections";
import { usePatientCase } from "../usePatientCase";
import { DiseaseCategoryType } from "fe-common/graphql";

type UseSuggestionsForPlanParams = {
  runPlan?: boolean;
  categories?: DiseaseCategoryType[];
  includeDiseaseMetrics?: boolean;
};

const defaultUseSuggestionsForPlanParams = {
  runPlan: true,
  includeDiseaseMetrics: true,
};

export function useSuggestionsForPlan({
  runPlan,
  includeDiseaseMetrics,
  categories,
}: UseSuggestionsForPlanParams = defaultUseSuggestionsForPlanParams) {
  const [suggestionState, setSuggestionState] = useSuggestionsState();
  const [
    { planDirty, lastPlanCategory },
    setDiagnosticAgentState,
  ] = useDiagnosticAgentState();

  const { featuresSections, ddData } = useDiseasesSections();
  const { patientCase } = usePatientCase();
  const { diseasesToIncludeInPlan, diseasesToRemoveFromPlan } = patientCase;

  const onDone = () => {
    setDiagnosticAgentState((prevState) => ({
      ...prevState,
      lastPlanCategory: categories,
    }));
  };

  const diseases = featuresSections[0].elements;

  // Max why don't we use the inPlanSections from useDiseasesInSection?
  const diseasesInPlan = useMemo(() => {
    const allDiseases = featuresSections.flatMap((section) => section.elements);
    const diseasesWithSubTypesInlcudedInPlan = allDiseases?.filter((q) =>
      diseasesToIncludeInPlan?.some((qf) => qf.id === q.qf.id),
    );
    return diseases
      .filter((q) => !diseasesToRemoveFromPlan?.some((qf) => qf.id === q.qf.id))
      .concat(diseasesWithSubTypesInlcudedInPlan);
  }, [
    diseases,
    diseasesToRemoveFromPlan,
    diseasesToIncludeInPlan,
    featuresSections,
  ]);

  const categoryChange =
    !lastPlanCategory ||
    !categories ||
    categories.some((cat) => !lastPlanCategory.includes(cat));

  const planNeeded =
    runPlan &&
    (planDirty || categoryChange) &&
    !!ddData &&
    (diseasesInPlan?.length > 0 || diseasesToIncludeInPlan?.length > 0);

  const {
    findingSuggestions,
    suggestionDebug,
    planIsLoading,
    clusteredSuggestions,
  } = useSuggestions({
    patientCase,
    diseases: diseasesInPlan,
    planNeeded,
    onDone,
    diseasesToIncludeInPlan,
    initialSuggestionState: suggestionState,
    categories,
    includeDiseaseMetrics,
  });

  useEffect(
    () =>
      setSuggestionState({
        findingSuggestions,
        suggestionDebug,
        planIsLoading,
        clusteredSuggestions,
      }),
    [
      setSuggestionState,
      findingSuggestions,
      suggestionDebug,
      planIsLoading,
      clusteredSuggestions,
    ],
  );

  return {
    findingSuggestions,
    suggestionDebug,
    planIsLoading: planIsLoading,
    clusteredSuggestions,
  };
}
